// Which league season the public site leads with, and which one is shown as a recap.
// Data lives in season.json (shared with the Mac scrapers). Roll the JSON forward
// when a new season starts; nothing else in the site hardcodes a season.
import cfg from './season.json';
import type { CurrentLeague, Player } from '@/types';

export const SEASON = cfg;

const currentRe = new RegExp(cfg.current.seasonRegex, 'i');
const recapRe = new RegExp(cfg.recap.seasonRegex, 'i');

/** "Fall 2026" style season string belongs to the season we're tracking now. */
export const isCurrentSeason = (season?: string | null): boolean => currentRe.test((season || '').trim());
/** Season string belongs to the just-finished season shown as a recap. */
export const isRecapSeason = (season?: string | null): boolean => recapRe.test((season || '').trim());

/** Publicly showable live league rows (coach-hidden and unresolved same-name rows drop out). */
export const liveLeagues = (p: Player): CurrentLeague[] =>
  (p.currentLeagues || []).filter((l) => !l.ambiguous && !l.hidden && l.games > 0);

const mostGames = (rows: CurrentLeague[]): CurrentLeague | null =>
  [...rows].sort((a, b) => b.games - a.games)[0] || null;

/** The bowler's main league line for the current season (most games), if bowling. */
export const currentSeasonLine = (p: Player): CurrentLeague | null =>
  mostGames(liveLeagues(p).filter((l) => isCurrentSeason(l.season)));

/** The bowler's main line from the recap season, while LeagueSecretary still lists it. */
export const recapSeasonLine = (p: Player): CurrentLeague | null =>
  mostGames(liveLeagues(p).filter((l) => isRecapSeason(l.season)));

/** What a card leads with: current season first, else the recap season, else nothing. */
export const leagueLine = (p: Player): CurrentLeague | null => currentSeasonLine(p) || recapSeasonLine(p);

/** Label for a league line's average ("Fall Avg" / "Summer Avg" / "League Avg"). */
export const avgLabelFor = (l: CurrentLeague | null): string =>
  !l ? cfg.current.avgLabel : isCurrentSeason(l.season) ? cfg.current.avgLabel : isRecapSeason(l.season) ? cfg.recap.avgLabel : 'League Avg';

/** Short date for a LeagueSecretary week ("2026-08-29T00:00:00" -> "Aug 29"). */
export const weekDate = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
